import { useEffect, useState } from "react";
import { Alert, Button, Card, Descriptions, Form, Input, InputNumber, Space, Table, Tag, TimePicker, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { AttendanceRuleSet } from "../../api-models";

// Rule sets are versioned and additive (attendance_rules.go): publishing writes a new
// version and deactivates the previous one, so this page never edits an existing row.
// Only the standard window, break, rounding and reasonableness limit are data; the
// "every hour on a Friday or public holiday is overtime" branch is fixed in computeHours
// and is spelled out here so nobody expects to retune it from the form.

const TIME_FORMAT = "HH:mm";

const minutesToTime = (minutes: number): Dayjs => dayjs().startOf("day").add(minutes, "minute");
const timeToMinutes = (value: Dayjs): number => value.hour() * 60 + value.minute();

function formatMinutes(minutes: number): string {
  return minutesToTime(minutes).format(TIME_FORMAT);
}

interface RuleFormValues {
  window: [Dayjs, Dayjs];
  breakDeductionMinutes: number;
  roundingMinutes: number;
  reasonablenessLimitHours: number;
  notes?: string;
}

export default function AttendanceRulesPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Settings.Write");
  const { request, loading } = useApi();
  const [ruleSets, setRuleSets] = useState<AttendanceRuleSet[]>([]);
  const [form] = Form.useForm<RuleFormValues>();

  const active = ruleSets.find((rs) => rs.active);

  async function load() {
    const res = await request<AttendanceRuleSet[]>({ url: "/attendance-rule-sets" });
    setRuleSets(res);
    return res;
  }

  useEffect(() => {
    // The form starts from the active version so publishing is "change one thing",
    // not "retype the whole policy".
    load().then((res) => {
      const current = res.find((rs) => rs.active);
      if (!current) return;
      form.setFieldsValue({
        window: [minutesToTime(current.standardStartMinute), minutesToTime(current.standardEndMinute)],
        breakDeductionMinutes: current.breakDeductionMinutes,
        roundingMinutes: current.roundingMinutes,
        reasonablenessLimitHours: current.reasonablenessLimitHours,
        notes: "",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit() {
    const values = await form.validateFields();
    const [start, end] = values.window;
    await request({
      method: "POST",
      url: "/attendance-rule-sets",
      data: {
        standardStartMinute: timeToMinutes(start),
        standardEndMinute: timeToMinutes(end),
        breakDeductionMinutes: values.breakDeductionMinutes ?? 0,
        roundingMinutes: values.roundingMinutes ?? 0,
        reasonablenessLimitHours: values.reasonablenessLimitHours ?? 16,
        notes: values.notes ?? "",
      },
    });
    message.success("New rule set published and made active.");
    form.setFieldsValue({ notes: "" });
    load();
  }

  return (
    <div>
      <PageHeader
        title="Attendance Rules"
        subtitle="Standard working window, break, rounding and the overtime split applied to every attendance line"
      />

      <Card size="small" title={active ? `Active rules (version ${active.version})` : "Active rules"} style={{ marginBottom: 16 }}>
        {active ? (
          <>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Standard window">
                {formatMinutes(active.standardStartMinute)} - {formatMinutes(active.standardEndMinute)}
              </Descriptions.Item>
              <Descriptions.Item label="Break deduction">
                {active.breakDeductionMinutes ? `${active.breakDeductionMinutes} min` : "None"}
              </Descriptions.Item>
              <Descriptions.Item label="Rounding">
                {active.roundingMinutes ? `Nearest ${active.roundingMinutes} min` : "None (exact minutes)"}
              </Descriptions.Item>
              <Descriptions.Item label="Reasonableness limit">
                {active.reasonablenessLimitHours ? `${active.reasonablenessLimitHours} h gross` : "Not enforced"}
              </Descriptions.Item>
              <Descriptions.Item label="In force since">{dayjs(active.createdAt).format(DT_FORMATS.dateTime)}</Descriptions.Item>
              <Descriptions.Item label="Notes">{active.notes || "-"}</Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message="How these rules split a shift"
              description={
                <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                  <li>
                    On a working day, hours worked after <strong>{formatMinutes(active.standardEndMinute)}</strong> are overtime.
                    Everything before it is regular, including hours worked before the {formatMinutes(active.standardStartMinute)}{" "}
                    start - an early start does not by itself create overtime.
                  </li>
                  <li>
                    On a <strong>Friday or a public holiday</strong>, every hour worked is overtime. This is fixed policy and cannot be
                    changed from this page.
                  </li>
                  <li>Break minutes come out of the regular portion only, so regular + overtime always equals gross.</li>
                  <li>
                    A line whose gross hours exceed the reasonableness limit is flagged as an exception for the supervisor to explain -
                    it is never blocked or trimmed.
                  </li>
                  <li>
                    Overtime is always computed from the clock times. It cannot be typed in, and it is recalculated whenever a line is
                    edited or a sheet is reopened. Sheets already submitted keep the rules and calendar classification they were
                    calculated under.
                  </li>
                </ul>
              }
            />
          </>
        ) : (
          <Typography.Text type="secondary">No rule set is active.</Typography.Text>
        )}
      </Card>

      {canWrite && (
        <Card size="small" title="Publish a new version" style={{ marginBottom: 16 }}>
          <Typography.Paragraph type="secondary">
            Rules are versioned, never edited. Publishing creates the next version and makes it active; the current one is retained for
            audit. Sheets already submitted are not recalculated.
          </Typography.Paragraph>
          <Form form={form} layout="vertical" onFinish={submit} style={{ maxWidth: 640 }}>
            <Form.Item
              name="window"
              label="Standard working window"
              rules={[{ required: true, message: "Set the standard start and end time." }]}
              extra="The end time is the overtime boundary on a normal working day."
            >
              <TimePicker.RangePicker format={TIME_FORMAT} minuteStep={5} needConfirm={false} />
            </Form.Item>
            <Space size="large" wrap align="start">
              <Form.Item name="breakDeductionMinutes" label="Break deduction (minutes)" initialValue={0}>
                <InputNumber min={0} max={480} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="roundingMinutes" label="Rounding (minutes)" initialValue={0} extra="0 means exact minutes.">
                <InputNumber min={0} max={60} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="reasonablenessLimitHours"
                label="Reasonableness limit (gross hours)"
                initialValue={16}
                extra="Flags the line, never blocks it."
              >
                <InputNumber min={1} max={24} step={0.5} style={{ width: 200 }} />
              </Form.Item>
            </Space>
            <Form.Item name="notes" label="Notes" extra="Why this change was made - shown in the version history and the audit log.">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Publish new version
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Card size="small" title="Version history">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={ruleSets}
          columns={[
            {
              title: "Version",
              dataIndex: "version",
              render: (v: number, row: AttendanceRuleSet) => (
                <Space>
                  <span>v{v}</span>
                  {row.active && <Tag color="green">ACTIVE</Tag>}
                </Space>
              ),
            },
            {
              title: "Standard window",
              render: (_, row: AttendanceRuleSet) => `${formatMinutes(row.standardStartMinute)} - ${formatMinutes(row.standardEndMinute)}`,
            },
            { title: "Break", dataIndex: "breakDeductionMinutes", render: (v: number) => (v ? `${v} min` : "-") },
            { title: "Rounding", dataIndex: "roundingMinutes", render: (v: number) => (v ? `${v} min` : "-") },
            { title: "Limit", dataIndex: "reasonablenessLimitHours", render: (v: number) => (v ? `${v} h` : "-") },
            { title: "Created", dataIndex: "createdAt", render: (v: string) => dayjs(v).format(DT_FORMATS.dateTime) },
            { title: "Notes", dataIndex: "notes", render: (v: string) => v || "-" },
          ]}
        />
      </Card>
    </div>
  );
}
